import {
  Button, Loading, useRecordContext, useTranslate,
} from 'react-admin';
import {
  DialogTitle, DialogContent, Dialog,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  DialogActions,
  Typography,
} from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { Asset, CertificationProperties } from '@x-guard/xgac-types/xgac';
import { xgacDataProvider } from '../../dataProviders/xgacDataProvider';
import { certificationConsideredExpiringMilliseconds } from '../../utils/assetCertifications';
import { CertificationExpiryWithDaysCaption } from '../CertificationExpiryWithDaysCaption';

type CertifiedUserInfo = {
  _id: string;
  name: string;
  firstIssuedOn: string;
  lastReceivedOn: string;
  expiryDate: string;
};

// disabled because the label is simply added to display the column name in the datagrid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const CertifiedUsersButton = (props: { label?: string }) => {

  const record = useRecordContext();
  const translate = useTranslate();
  const [certifiedUsersDialogOpen, setCertifiedUsersDialogOpen] = useState(false);
  const [expiringCertificationsDialogOpen, setExpiringCertificationsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [certifiedUsers, setCertifiedUsers] = useState<CertifiedUserInfo[]>([]);

  useEffect(() => {

    setLoading(true);
    xgacDataProvider.getList('all-assets', {
      pagination: { page: 1, perPage: -1 },
      sort: { field: 'name', order: 'ASC' },
      filter: { 'certifications.certificationCategory._id': record?._id },
    }).then((response) => {

      const mappedAssets = response.data.map((asset: Asset) => {

        // Only the certifications for the current certification category are considered
        const certificationOfCategory = (asset.certifications ?? [])
          .find((certification: CertificationProperties) => certification.certificationCategory._id === record?._id);
        const firstIssuedOn = certificationOfCategory?.firstIssuedOn || '';
        const lastReceivedOn = certificationOfCategory?.lastReceivedOn || '';
        const expiryDate = certificationOfCategory?.expiryDate || '';
        return {
          _id: asset._id,
          name: asset.name,
          firstIssuedOn,
          lastReceivedOn,
          expiryDate,
        };

      });

      setCertifiedUsers(mappedAssets);
      setLoading(false);

    });

  }, [record?._id]);

  const expiringCertifications = useMemo(() => {

    const now = new Date().getTime();
    return certifiedUsers.filter((asset) => new Date(asset.expiryDate).getTime() - now < certificationConsideredExpiringMilliseconds);

  }, [certifiedUsers]);

  // This helper function returns the body of the dialog, depending on the loading state and whether there are any certified users
  function returnDialogBody(certificationsInfo: CertifiedUserInfo[]) {

    if (loading) {

      return <Loading loadingPrimary={translate('general.text.loading')} loadingSecondary={translate('general.text.loading_secondary')}/>;

    }
    if (certificationsInfo.length === 0) {

      return <Typography sx={{ textAlign: 'center', marginTop: '20px' }}>
        {translate('resources.certification-categories.text.certified_users_dialog_empty')}</Typography>;

    }

    return <>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{translate('general.fields.name')}</TableCell>
            <TableCell>{translate('resources.assets.certifications.fields.first_issued_on')}</TableCell>
            <TableCell>{translate('resources.assets.certifications.fields.last_received_on')}</TableCell>
            <TableCell>{translate('resources.assets.certifications.fields.expiry_date')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {certificationsInfo.map((asset) => (
            <TableRow key={asset._id}>
              <TableCell>{asset.name}</TableCell>
              <TableCell>{asset.firstIssuedOn ? new Date(asset.firstIssuedOn).toLocaleDateString() : ''}</TableCell>
              <TableCell>{asset.lastReceivedOn ? new Date(asset.lastReceivedOn).toLocaleDateString() : ''}</TableCell>
              <TableCell>
                <CertificationExpiryWithDaysCaption record={{ ...asset, id: asset._id }}/>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>;

  }

  return (<>
    <div style={{ display: 'flex', flexDirection: 'row', gap: '5px' }}>
      <Button
        variant={'contained'}
        disabled={certifiedUsers.length === 0}
        onClick={() => setCertifiedUsersDialogOpen(true)}
        label={`${certifiedUsers.length} ${translate('resources.certification-categories.text.certified_users')}`}
        sx={{ whiteSpace: 'nowrap' }}
      />
      <Button
        variant={'contained'}
        color={'warning'}
        disabled={expiringCertifications.length === 0}
        onClick={() => setExpiringCertificationsDialogOpen(true)}
        label={`${expiringCertifications.length} ${translate('resources.certification-categories.text.expiring')}`}
        sx={{ whiteSpace: 'nowrap' }}
      />
    </div>
    <Dialog open={certifiedUsersDialogOpen} onClose={() => setCertifiedUsersDialogOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle>{`${translate('resources.certification-categories.text.certified_users_dialog_title')} "${record?.name ?? ''}"`}</DialogTitle>
      <DialogContent>
        {returnDialogBody(certifiedUsers)}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCertifiedUsersDialogOpen(false)} label={translate('general.text.close')}/>
      </DialogActions>
    </Dialog>
    <Dialog open={expiringCertificationsDialogOpen} onClose={() => setExpiringCertificationsDialogOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle>{`${translate('resources.certification-categories.text.expiring_certifications_dialog_title')} "${record?.name ?? ''}"`}</DialogTitle>
      <DialogContent>
        {returnDialogBody(expiringCertifications)}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setExpiringCertificationsDialogOpen(false)} label={translate('general.text.close')}/>
      </DialogActions>
    </Dialog>
  </>);

};
